import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, Apple, Play } from "lucide-react";
import { APP_STORE_URL, PLAY_STORE_URL } from "@/data/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1722865087103-c1904afc8f82?q=80&w=1920&auto=format&fit=crop";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, delay: 0.35 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskedLine = ({ children, index, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      variants={line}
      custom={index}
      initial="hidden"
      animate="show"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 pt-28 pb-20"
      data-testid="hero-section"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY }} aria-hidden="true">
        <img
          src={HERO_IMG}
          alt=""
          className="h-[120%] w-full scale-110 object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/65" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-transparent to-[#050505]" />
      </motion.div>

      <div className="pointer-events-none absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-[#FF3333]/20 blur-[120px]" aria-hidden="true" />
      <div className="pointer-events-none absolute -right-32 bottom-1/4 h-96 w-96 rounded-full bg-[#00F0FF]/10 blur-[120px]" aria-hidden="true" />

      <motion.div className="relative z-10 flex flex-col items-center text-center" style={{ opacity: fade }}>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="label-tech mb-8 flex items-center gap-3 text-[#00F0FF]"
          data-testid="hero-eyebrow"
        >
          <span className="h-px w-8 bg-[#00F0FF]/60" aria-hidden="true" />
          The group-ride companion for cars & bikes
          <span className="h-px w-8 bg-[#00F0FF]/60" aria-hidden="true" />
        </motion.p>

        <h1
          className="font-display text-[13.5vw] font-black leading-[0.95] tracking-tighter text-white sm:text-7xl md:text-8xl lg:text-[7.5rem]"
          data-testid="hero-headline"
        >
          <MaskedLine index={0}>RIDE TOGETHER,</MaskedLine>
          <MaskedLine index={1}>
            <span className="text-[#FF3333]">ARRIVE</span> TOGETHER.
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-neutral-300 sm:text-lg"
          data-testid="hero-subheadline"
        >
          JomKonvoi keeps your whole convoy connected on the road — live positions on one shared
          map, regroup alerts when someone drops back, and a route everyone follows. Cars and
          motorcycles, one crew, zero lost riders.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-col items-center gap-4 sm:flex-row"
        >
          <motion.button
            onClick={() => window.__lenis?.scrollTo("#how-it-works", { offset: -70 })}
            whileTap={{ scale: 0.95 }}
            whileHover={{ scale: 1.03 }}
            className="inline-flex items-center gap-3 rounded-full bg-[#FF3333] px-8 py-4 font-display text-base font-bold text-white shadow-[0_0_40px_rgba(255,51,51,0.35)] transition-colors duration-300 hover:bg-[#FF5C5C] focus:outline-none focus:ring-2 focus:ring-[#FF3333] focus:ring-offset-2 focus:ring-offset-black"
            data-testid="hero-how-it-works-btn"
          >
            See how it works
            <ArrowRight className="h-5 w-5" />
          </motion.button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-8 flex flex-wrap items-center justify-center gap-3"
          data-testid="store-badges"
        >
          <motion.a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-2.5 rounded-xl border border-white/15 bg-white/5 px-5 py-2.5 backdrop-blur-sm transition-colors duration-300 hover:border-white/35 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40"
            data-testid="app-store-badge"
          >
            <Apple className="h-6 w-6 fill-white text-white" />
            <span className="text-left leading-tight">
              <span className="block text-[10px] text-neutral-400">Download on the</span>
              <span className="block font-display text-sm font-bold text-white">App Store</span>
            </span>
          </motion.a>
          <motion.a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-2.5 rounded-xl border border-white/15 bg-white/5 px-5 py-2.5 backdrop-blur-sm transition-colors duration-300 hover:border-white/35 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40"
            data-testid="play-store-badge"
          >
            <Play className="h-6 w-6 fill-white text-white" />
            <span className="text-left leading-tight">
              <span className="block text-[10px] uppercase text-neutral-400">Get it on</span>
              <span className="block font-display text-sm font-bold text-white">Google Play</span>
            </span>
          </motion.a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 z-10 flex flex-col items-center gap-2 text-neutral-500"
        aria-hidden="true"
      >
        <span className="label-tech text-[10px]">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown className="h-4 w-4" />
        </motion.span>
      </motion.div>
    </section>
  );
};
